<script setup lang="ts">
import { computed } from 'vue';
import type { Customer } from '@/types/customer';
import AddressSelector from './AddressSelector.vue';

interface Props {
  customer: Customer;
  orderType: 'Delivery' | 'Takeaway';
  customerAddresses: any[];
  selectedAddressId?: number | undefined;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'clear-customer': [];
  'show-address-manager': [];
  'address-selected': [address: any];
}>();

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const shouldShowAddresses = computed(() => {
  return props.orderType === 'Delivery';
});
</script>

<template>
  <div class="bg-indigo-50/60 dark:bg-indigo-900/10 border border-indigo-200/60 dark:border-indigo-100/40 rounded-lg p-4 relative">
    <!-- Clear Customer Button - Top Right Corner -->
    <button
      class="absolute top-2 right-2 w-6 h-6 rounded bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-all duration-200 flex items-center justify-center p-1"
      title="Cambiar cliente"
      @click="$emit('clear-customer')"
    >
      <svg
        class="w-3 h-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </button>
    
    <!-- Customer Info -->
    <div class="pr-8">
      <h4 class="font-medium text-sm text-gray-900 dark:text-gray-100">
        {{ customer.name }}
      </h4>
      <p class="text-xs text-gray-600 dark:text-gray-400">
        {{ customer.phone }}
      </p>
      
      <!-- Address Selection for Customer with Addresses (Only for Delivery) -->
      <AddressSelector
        v-if="shouldShowAddresses"
        :customer-addresses="customerAddresses"
        :selected-address-id="selectedAddressId"
        @address-selected="$emit('address-selected', $event)"
        @show-address-manager="$emit('show-address-manager')"
      />
      
      <!-- Single Address Display (Only for Delivery) -->
      <div
        v-if="shouldShowAddresses && customer.full_address && customer.full_address !== 'Sin dirección'"
        class="mt-2"
      >
        <p class="text-xs text-gray-500 dark:text-gray-400">
          {{ customer.full_address }}
        </p>
      </div>
      
      <div class="flex items-center space-x-4 mt-2">
        <span
          v-if="customer.order_count > 0"
          class="text-xs text-gray-500 dark:text-gray-400"
        >
          {{ customer.order_count }} pedido(s)
        </span>
        <span
          v-if="customer.last_order_date"
          class="text-xs text-gray-500 dark:text-gray-400"
        >
          Último: {{ formatDate(customer.last_order_date) }}
        </span>
      </div>
      
      <!-- No Address Display -->
      <div
        v-if="(!customer.full_address || customer.full_address === 'Sin dirección') && customerAddresses.length === 0"
        class="mt-2"
      >
        <p class="text-xs text-gray-500 dark:text-gray-400">
          Sin direcciones registradas
        </p>
      </div>
    </div>
  </div>
</template>
